/**
 *
 * PasswordSetupPage
 *
 * This is the page we show when the user visits a url that doesn't have a route
 */

import { withStyles } from "@envestnet/envreact-component-library";
import classnames from "classnames";
import PasswordSetup from "components/PasswordSetup";
import UpdateUsername from "components/UpdateUsername";
import { UsernameUniqueErrorType } from "components/UpdateUsername/types";
import Logo from "images/Logo";
import React, { useEffect, useMemo, useState } from "react";
import { PasswordPolicy } from "types/PasswordPolicy";
import { UserInfo } from "types/UserInfo";
import { validateUsername } from "utils/usernameValidator";
import withIntl from "utils/withIntl";
import TryAgainDialog from "components/TryAgainDialog";
import AccountLocked from "components/AccountLocked";
import storage from "../../persistance";
import { getPasswordPolicyStatements } from "./getPasswordPolicies";
import messages from "./messages";
import styles, { Styles } from "./styles";
import { PasswordSetupTypes } from "./types";

type Props = Styles &
  PasswordSetupTypes & {
    formatMessage: FormatMessage;
    passwordPolicies: PasswordPolicy;
    passwordChangeError: Object;
    onSuccess: () => void;
    showGenericError: () => void;
    termsAndCondition?: boolean;
    setTermsAndCondition?: Function;
    userName: string;
    userInfo: UserInfo;
    loadPasswordPolicies: () => void;
    onUserActivation: Function;
    loadUserInfo: () => void;
    setGlobalUserInfo: (userInfo: UserInfo) => void;
    loadingUserInfo: boolean;
    loadingPasswordPolicies: boolean;
    passwordFailure?: boolean;
    isAccountActivated?: boolean;
    getTermsAndConditionFile: () => void;
    termsAndConditionContent: string;
    noTermsAndConditions: boolean;
    noOfRetries: number;
    setInvalidCode: Function;
    apiError: string | null;
    clearApiError: () => void;
    clearPasswordFailure: () => void;
    usernameUpdateError: string | null;
    clearUsernameUpdateError: () => void;
    validatingUsername: boolean;
    checkedUsername: string;
    isUsernameUnique: boolean | null;
    usernameValidationErrorType: UsernameUniqueErrorType;
    validateUsernameUnique: (payload: { username: string }) => void;
    resetUsernameValidation: () => void;
  };

const toValidationErrorMessage = (
  errorCode: ReturnType<typeof validateUsername>["errorCode"],
  formatMessage: FormatMessage
): string | null => {
  if (errorCode === "REQUIRED") {
    return formatMessage(messages.usernameValidationError);
  }

  if (errorCode === "FORMAT") {
    return formatMessage(messages.usernameValidationError);
  }

  return null;
};

const PasswordSetupPage = (props: Props) => {
  const {
    classes,
    style,
    formatMessage,
    passwordPolicies,
    passwordChangeError,
    onSuccess,
    showGenericError,
    termsAndCondition,
    noTermsAndConditions,
    setTermsAndCondition,
    userName,
    userInfo,
    loadPasswordPolicies,
    onUserActivation,
    loadUserInfo,
    setGlobalUserInfo,
    loadingPasswordPolicies,
    passwordFailure,
    isAccountActivated,
    getTermsAndConditionFile,
    termsAndConditionContent,
    noOfRetries,
    setInvalidCode,
    apiError,
    clearApiError,
    clearPasswordFailure,
    usernameUpdateError,
    clearUsernameUpdateError,
    checkedUsername,
    isUsernameUnique,
    usernameValidationErrorType,
    validateUsernameUnique,
    resetUsernameValidation,
  } = props;

  const [pendingSubmission, setPendingSubmission] = useState<any>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(true);
  const [username, setUsername] = useState(userName || "");
  const [uiValidationError, setUiValidationError] = useState<string | null>(
    null
  );

  useEffect(() => {
    loadPasswordPolicies();
    loadUserInfo();
    getTermsAndConditionFile();
    return () => {
      storage.setItem("SESSION_KEY_RETRY", "");
    };
  }, []);

  useEffect(() => {
    setGlobalUserInfo(userInfo);
  }, [userInfo]);

  useEffect(() => {
    if (noOfRetries) {
      setShowConfirmModal(true);
    }
  }, [noOfRetries]);

  useEffect(() => {
    setUsername(userName || "");
    setUiValidationError(null);
    clearUsernameUpdateError();
    resetUsernameValidation();
  }, [userName]);

  const onUsernameChange = (value: string) => {
    setUsername(value);
    const result = validateUsername(value, userName);
    setUiValidationError(
      toValidationErrorMessage(result.errorCode, formatMessage)
    );
    clearUsernameUpdateError();
    if (result.normalizedValue !== checkedUsername) {
      resetUsernameValidation();
    }
  };

  const onUsernameBlur = () => {
    const result = validateUsername(username, userName);
    setUiValidationError(
      toValidationErrorMessage(result.errorCode, formatMessage)
    );

    if (!result.isValid) {
      return;
    }

    if (
      checkedUsername === result.normalizedValue &&
      (isUsernameUnique === true || usernameValidationErrorType === "DUPLICATE")
    ) {
      return;
    }

    validateUsernameUnique({ username: result.normalizedValue });
  };

  const uniqueValidationError = useMemo(() => {
    if (usernameValidationErrorType === "DUPLICATE") {
      return formatMessage(messages.duplicateError);
    }

    if (usernameValidationErrorType === "GENERIC") {
      return formatMessage(messages.genericValidationError);
    }

    return null;
  }, [usernameValidationErrorType]);

  const startPasswordChange = (value: any) => {
    const normalizedUsername = username.trim();
    const validationResult = validateUsername(normalizedUsername, userName);
    const isUnchangedUsername = validationResult.errorCode === "UNCHANGED";

    if (!validationResult.isValid && !isUnchangedUsername) {
      return;
    }

    const payload = userInfo.username_customization_enabled
      ? { ...value, username: normalizedUsername }
      : value;

    // The submit API independently validates uniqueness, so submission doesn't wait on the blur check.
    onUserActivation({ value: payload });
    setPendingSubmission(payload);
  };

  const passwordRules = useMemo(
    () => getPasswordPolicyStatements(passwordPolicies, formatMessage),
    [passwordPolicies]
  );

  const usernameErrorMessage =
    uiValidationError || usernameUpdateError || uniqueValidationError;
  const disablePasswordSubmit = !!usernameErrorMessage;

  return (
    <div className={classnames(classes, classes.container)} style={style}>
      <div>
        <Logo />
        <h4 className={classnames(classes.modalHeader, "header-font-family")}>
          {formatMessage(messages.changePasswordHeader)}
        </h4>

        {noOfRetries === 0 ? (
          <AccountLocked type="ERROR" />
        ) : (
          <>
            {userInfo.username_customization_enabled && (
              <div className={classes.currentUsernameField}>
                <UpdateUsername
                  username={username}
                  flow="ACTIVATION"
                  onUsernameChange={onUsernameChange}
                  onUsernameBlur={onUsernameBlur}
                  errorMessage={usernameErrorMessage}
                />
              </div>
            )}

            <PasswordSetup
              onSuccess={onSuccess}
              showGenericError={showGenericError}
              startPasswordChange={startPasswordChange}
              passwordChangeError={passwordChangeError}
              passwordRules={passwordRules}
              termsAndCondition={termsAndCondition}
              setTermsAndCondition={setTermsAndCondition}
              noTermsAndConditions={noTermsAndConditions}
              userName={userName}
              loading={loadingPasswordPolicies}
              passwordFailure={passwordFailure}
              userInfo={userInfo}
              isAccountActivated={isAccountActivated}
              fileDetails={termsAndConditionContent}
              apiError={apiError}
              clearApiError={clearApiError}
              clearPasswordFailure={clearPasswordFailure}
              submitDisabled={disablePasswordSubmit}
              hasUsernameError={usernameUpdateError !== null}
            />
            {noOfRetries > 0 && noOfRetries < 3 && (
              <TryAgainDialog
                showConfirmModal={showConfirmModal}
                onModalClose={() => {
                  setInvalidCode();
                }}
                onModalSuccess={() => {
                  onUserActivation({ value: pendingSubmission as any });
                  setShowConfirmModal(false);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default withStyles(styles)(withIntl(PasswordSetupPage));
